import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { LinearProgress } from "@mui/material";
import { TIMEOUT, FLUX_WEBHOOK_URL } from "../../constants/CommonConstants";
import { errorHandle } from "../../Common/errorHandle";
import cognitoAuth from "../../util/Cognito";
import "./CeResetPasswordDialog.css";
import { encrypt, toBase64Url } from "../../util/util";

const ULTIMATE = "Ultimate SI";
const JOBMANAGER = "JobManager";
const ALL = "all";
const SCHEDULE = "JobManagerSchedule";

function CeResetPasswordDialog(props) {
    const { show, handleClose, userId, userLicenseList, intlFormatMessage, openSnackbar, _this, isIncludeSchedule } = props;
    const [radioButtonvalue, setRadioButtonvalue] = useState(JOBMANAGER);
    const [ultimateRadioButtonDisabled, setUltimateRadioButtonDisabled] = useState(false);
    const [jobManagerRadioButtonDisabled, setJobManagerRadioButtonDisabled] = useState(false);
    const [jobManagerScheduleRadioButtonDisabled, setJobManagerScheduleRadioButtonDisabled] = useState(false);
    const [isProgress, setIsProgress] = useState(false);

    useEffect(() => {
        if (show) {
            if (Array.isArray(userLicenseList)) {
                const isUltimateUser = userLicenseList.find((license) => license.feature === ULTIMATE);
                const isJobManagerUser = userLicenseList.find((license) => license.feature === JOBMANAGER);
                // 初期選択ラジオボタン
                if (isJobManagerUser && !isIncludeSchedule) {
                    setRadioButtonvalue(JOBMANAGER);
                } else if (isUltimateUser) {
                    setRadioButtonvalue(ULTIMATE);
                } else if(isIncludeSchedule){
                    setRadioButtonvalue(SCHEDULE);
                }

                // ラジオボタン活性状態
                setUltimateRadioButtonDisabled(!isUltimateUser);
                setJobManagerRadioButtonDisabled(!isJobManagerUser || isIncludeSchedule);
                setJobManagerScheduleRadioButtonDisabled(!isIncludeSchedule);
            }
        }
    }, [show, userId, isIncludeSchedule]);

    const handleChange = (event) => {
        setRadioButtonvalue(event.target.value);
    };

    const sendPasswordResetRequest = () => {
        let header = {
            Authorization: "",
        };
        setIsProgress(true);
        cognitoAuth
            .getAuthorizationToken()
            .then(
                (token) => {
                    header.Authorization = token;
                },
                (error) => {
                    setIsProgress(false);
                    throw error;
                }
            )
            .then(() => {
                const controller = new AbortController();
                const timer = window.setTimeout(
                    errorHandle(function () {
                        controller.abort();
                        throw _this;
                    }),
                    TIMEOUT
                );

                let feature = [];
                switch (radioButtonvalue) {
                    case ULTIMATE:
                        feature = [ULTIMATE];
                        break;
                    case JOBMANAGER:
                        feature = [JOBMANAGER];
                        break;
                    case ALL:
                        feature = [ULTIMATE, JOBMANAGER];
                        break;
                    case SCHEDULE:
                        feature = [SCHEDULE];
                        break;
                    default:
                        break;
                }

                let userQs = new URLSearchParams({
                    userId: userId,
                    feature: feature,
                });
                fetch(process.env.REACT_APP_CE_USER_ACCOUNT + `?${userQs}`, {
                    method: "GET",
                    headers: header,
                    signal: controller.signal,
                })
                    .then((res) => {
                        window.clearTimeout(timer);
                        if (!res.ok) {
                            if (res.status === 400) {
                                openSnackbar(intlFormatMessage({ id: "ce_error_400" }));
                            } else if (res.status === 409) {
                                openSnackbar(intlFormatMessage({ id: "ce_error_409" }));
                                document.location.reload();
                            } else {
                                openSnackbar(intlFormatMessage({ id: "server_error" }));
                            }
                        }
                        return res.json();
                    })
                    .then((response) => {
                        const ultimate = response.UltimateSI;
                        const jobManager = response.JobManager;
                        if (ultimate && jobManager) {
                            window.location.href =
                                "mailto:" +
                                ultimate.mailAddress +
                                "?subject=[Available] AccurioPro Dashboard JobManager and Printer Performance Analysis tool" +
                                "&body=" +
                                intlFormatMessage({ id: "customer_email_text_all" }, { email: ultimate.mailAddress, ultimateUserId: userId, ultimatePassword: ultimate.password, jobManagerUserId: userId, jobManagerPassword: jobManager.password });
                        } else if (ultimate) {
                            window.location.href =
                                "mailto:" +
                                ultimate.mailAddress +
                                "?subject=[Available] Printer Performance Analysis tool" +
                                "&body=" +
                                intlFormatMessage({ id: "customer_email_text" }, { email: ultimate.mailAddress, userId: userId, password: ultimate.password });
                        } else if (jobManager) {
                            if(jobManager.isFlux){
                                const encryptedCompanyId = toBase64Url(encrypt(userId));
                                const webhookUrl = FLUX_WEBHOOK_URL[process.env.REACT_APP_BUILD_ENV]+ "?companyId="+encryptedCompanyId;
                                window.location.href =
                                    "mailto:" +
                                    jobManager.mailAddress +
                                    "?subject=[Available] AccurioPro Dashboard JobManager" +
                                    "&body=" +
                                    intlFormatMessage({ id: "customer_email_text_with_flux_webhook" }, { email: jobManager.mailAddress, userId: userId, password: jobManager.password, webhookUrl:webhookUrl });
                            }else{
                                window.location.href =
                                    "mailto:" +
                                    jobManager.mailAddress +
                                    "?subject=[Available] AccurioPro Dashboard JobManager" +
                                    "&body=" +
                                    intlFormatMessage({ id: "customer_email_text_job_manager" }, { email: jobManager.mailAddress, userId: userId, password: jobManager.password });
                            }
                        }
                    })
                    .finally(() => {
                        setIsProgress(false);
                        handleClose();
                    });
            })
            .catch(() => {
                cognitoAuth.sessionTimeOut(_this.props);
                setIsProgress(false);
            });
    };

    return (
        <Modal id="ceResetPasswordDialog" className="jobManagerPage" show={show} onHide={handleClose} centered backdrop="static">
            {isProgress && <LinearProgress />}
            <Modal.Header closeButton>
                <Modal.Title>
                    {
                        <div className="content-title">
                            <span>{intlFormatMessage({ id: "ce_reset_password_dialog_title" })}</span>
                        </div>
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormControl sx={{ pl: 2 }}>
                    <RadioGroup aria-labelledby="resetFeatureRadioButtonsGroup" value={radioButtonvalue} name="resetFeatureRadioButtonsGroup" onChange={handleChange}>
                        <FormControlLabel value={JOBMANAGER} control={<Radio size="small" sx={{ p: 0 }} disabled={jobManagerRadioButtonDisabled} />} label={intlFormatMessage({ id: "ce_reset_password_dialog_job_manager" })} />
                        <FormControlLabel value={ULTIMATE} control={<Radio size="small" sx={{ p: 0 }} disabled={ultimateRadioButtonDisabled} />} label={intlFormatMessage({ id: "ce_reset_password_dialog_ultimate" })} />
                        <FormControlLabel
                            value={ALL}
                            control={<Radio size="small" sx={{ p: 0 }} />}
                            label={intlFormatMessage({ id: "ce_reset_password_dialog_all" })}
                            disabled={ultimateRadioButtonDisabled || jobManagerRadioButtonDisabled}
                        />
                        <FormControlLabel value={SCHEDULE} control={<Radio size="small" sx={{ p: 0 }} disabled={jobManagerScheduleRadioButtonDisabled} />} label={intlFormatMessage({ id: "ce_reset_password_dialog_job_manager_schedule" })} />
                    </RadioGroup>
                </FormControl>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" className="primaryBtn" onClick={sendPasswordResetRequest}>
                    {intlFormatMessage({ id: "header_ok" })}
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    {intlFormatMessage({ id: "header_cancel" })}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default CeResetPasswordDialog;
